import { useMutation } from '@tanstack/react-query';
import type { AgentApiCandidateProfilesParseDocument } from '@/types/serializers';

/** 書類解析エラーレスポンス */
type ParseDocumentErrorResponse = {
  success: false;
  error: string;
};

/**
 * 履歴書・職務経歴書（PDF/Word）を解析して候補者情報を返す mutation フック。
 * POST /agent/api/candidate_profiles/parse_document (multipart/form-data, file)
 * - 成功: AgentApiCandidateProfilesParseDocument
 * - 失敗: エラーメッセージを含む Error をスロー
 */
export default function useParseCandidateDocument() {
  const token = (document.getElementsByName('csrf-token')[0] as HTMLMetaElement).content;

  return useMutation({
    mutationFn: async (file: File): Promise<AgentApiCandidateProfilesParseDocument> => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/agent/api/candidate_profiles/parse_document', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': token,
        },
        body: formData,
      });

      if (!response.ok) {
        // 非JSONレスポンス（ALB/Pumaタイムアウト等のHTMLエラー）を安全に処理するため
        // content-typeを確認してJSONの場合のみパースする
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          const errorData: ParseDocumentErrorResponse = await response.json();
          throw new Error(errorData.error ?? '書類の解析に失敗しました');
        }
        throw new Error('書類の解析に失敗しました');
      }

      const data: AgentApiCandidateProfilesParseDocument = await response.json();
      return data;
    },
  });
}
